/**
 * The crosshair link: a runtime channel between the charts mounted on one
 * layout, carrying the date under the pointer (`SyncFlags.crosshair`, owner
 * 2026-10-05). `window-link.ts`'s shape, for its reason — the pointer moves
 * sixty times a second, so it is a channel and never a contract field; only the
 * tick that turns it on is stored.
 *
 * **A date, not a bar index**, so a weekly chart beside a daily one lands on the
 * week that holds the day (the receiving builder's `showLinkedTime` picks the
 * containing bar). `null` means the pointer left, and every peer hides its line.
 *
 * **No echo to stop.** Unlike a window, receiving a date draws an annotation and
 * moves no pointer, so a peer never publishes back. The one de-duplication kept
 * is the same date twice in a row, which is most pointer moves once the line
 * snaps to a bar.
 *
 * Module scope with subscribers, `symbol-index.ts`'s shape: the members are
 * siblings in different subtrees, and nothing about it belongs in React state.
 */

type Apply = (time: number | null) => void;

const channels = new Map<string, Map<string, Apply>>();
/** The last date each link carried; `undefined` before anything has. */
const lastCarried = new Map<string, number | null>();

/** Join `link` as `member`; returns the leave function. */
export function joinCrosshairLink(link: string, member: string, apply: Apply): () => void {
  let members = channels.get(link);
  if (!members) channels.set(link, (members = new Map()));
  members.set(member, apply);
  return () => {
    const now = channels.get(link);
    if (now?.get(member) !== apply) return;
    now.delete(member);
    if (now.size === 0) {
      channels.delete(link);
      lastCarried.delete(link);
    }
  };
}

/** Tell every other member of `link` the date under `member`'s pointer, or `null`. */
export function publishCrosshair(link: string, member: string, time: number | null): void {
  const members = channels.get(link);
  if (!members?.has(member)) return;
  if (lastCarried.has(link) && lastCarried.get(link) === time) return;
  lastCarried.set(link, time);
  for (const [id, apply] of members) if (id !== member) apply(time);
}

/** Whether a layout's crosshair link is on — it defaults ON, so absent is on. */
export function crosshairLinked(sync: { crosshair?: boolean } | undefined): boolean {
  return sync?.crosshair !== false;
}
